import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  Box,
  Typography,
  IconButton,
  Collapse,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import moment from 'moment';
import { Schedule } from '../../pages/Dashboard/Dashboard';

interface ScheduleDeadlineSnackbarProps {
  /** Whether the snackbar should be shown */
  open?: boolean;
  /** Callback when user dismisses the snackbar */
  onClose?: () => void;
  /** Callback when user clicks "Submit Schedule" */
  onSubmitSchedule?: () => void;
  /** The deadline date/time (defaults to end of today) */
  deadline?: string | Date;
  /** Auto-hide duration in milliseconds (0 = no auto-hide) */
  autoHideDuration?: number;
  /** Position of the snackbar */
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  /** Show countdown timer */
  showCountdown?: boolean;
  /** Custom message override */
  customMessage?: string;
  schedule?: Schedule | null;
}

const ScheduleDeadlineSnackbar: React.FC<ScheduleDeadlineSnackbarProps> = ({
  open: controlledOpen,
  onClose,
  onSubmitSchedule,
  deadline = moment().endOf('day').toDate(),
  autoHideDuration = 0,
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
  showCountdown = true,
  customMessage,
  schedule
}) => {
  const [internalOpen, setInternalOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [percentageRemaining, setPercentageRemaining] = useState(100);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  useEffect(() => {
    if (!showCountdown) return;

    const updateCountdown = () => {
      const now = moment();
      const deadlineMoment = moment(deadline);
      const diff = deadlineMoment.diff(now);

      if (diff <= 0) {
        setTimeRemaining('Deadline passed');
        setPercentageRemaining(0);
        return;
      }

      const duration = moment.duration(diff);
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s remaining`);
      } else {
        setTimeRemaining(`${seconds}s remaining`);
      }

      // Calculate percentage (assuming deadline is at end of day)
      const startOfDay = moment().startOf('day');
      const totalTime = deadlineMoment.diff(startOfDay);
      const elapsed = now.diff(startOfDay);
      const percentage = Math.max(0, Math.min(100, ((totalTime - elapsed) / totalTime) * 100));
      setPercentageRemaining(percentage);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [deadline, showCountdown]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    if (!isControlled) {
      setInternalOpen(false);
    }

    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (onSubmitSchedule) {
      onSubmitSchedule();
    }
    handleClose();
  };

  const getScheduleRange = () => {
    const start = moment(schedule?.activeScheduleDate)
    const end = moment(schedule?.activeScheduleDate).add(schedule?.activeScheduleFrequency === "biweekly"
        ? 2 : 1, schedule?.activeScheduleFrequency !== "monthly" ? "week" : "month")
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  };

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={autoHideDuration || null}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      sx={{ width: "75%" }}
    >
      <Alert
        severity="info"
        icon={<WarningIcon />}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          width: '100%',
          alignItems: 'flex-start',
          '& .MuiAlert-message': {
            width: '100%',
            pt: 0.5
          }
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Box sx={{ flex: 1 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                Please Review Schedule
                <Typography variant="body2">
                  {customMessage || `Please review, finalize, and submit the schedule for staff to view`}
                </Typography>
              </AlertTitle>
            </Box>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ ml: 1, mt: -0.5 }}
            >
              {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            {showCountdown && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {timeRemaining}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Due: {moment(deadline).format('h:mm A')}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentageRemaining}
                  color={percentageRemaining > 50 ? 'warning' : 'error'}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.3)'
                  }}
                />
              </Box>
            )}

            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" gutterBottom>
                <strong>What happens next?</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 1, mb: 2 }}>
                <li>Schedules get submitted for staff to view automatically the day prior</li>
                <li>You can still make changes after submission</li>
                <li>Staff can only view the next schedule after it is submitted</li>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                <CalendarIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                Schedule for: {getScheduleRange()}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  fontWeight: 600
                }}
              >
                Submit Schedule
              </Button>
              <Button
                size="small"
                variant="text"
                color="primary"
                onClick={handleClose}
              >
                Remind Me Later
              </Button>
            </Box>
          </Collapse>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default ScheduleDeadlineSnackbar;